import React, { MouseEventHandler, ReactNode } from 'react';
import BaseDialog from './BaseDialog';
import { mui } from '~/tools/mui';
import { jss } from '~/tools/jss';
import { useTranslation } from '~/behaviors';
import Button from '~/components/Button';

const useStyles = jss.makeStyles((theme) => ({
  content: {
    minWidth: 450,
    display: 'flex',
    maxWidth: 700,
  },
  iconContainer: {
    width: 64,
    height: 64,
    marginRight: 24,
  },
  typographyContainer: {
    padding: '8px 0',
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    marginBottom: 16,
  },
  message: {
    fontSize: 16,
    whiteSpace: 'pre-line',
  },
}));

type ConfirmDialogProps = {
  title?: string;
  message: string;
  isVisible: boolean;
  onConfirm: MouseEventHandler;
  onCancel: MouseEventHandler;
  Icon?: ReactNode;
  confirmButtonLabel: string;
  cancelButtonLabel?: string;
  disabled?: boolean;
};

const ConfirmDialog = ({
  title,
  message,
  isVisible,
  Icon = null,
  onConfirm,
  onCancel,
  confirmButtonLabel,
  cancelButtonLabel,
  disabled,
}: ConfirmDialogProps) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const TitleSection = () => (
    <div className={styles.content} data-testid="dialog-title">
      {Icon && <div className={styles.iconContainer}>{Icon}</div>}
      <div className={styles.typographyContainer}>
        {title && (
          <mui.Typography variant="h1" className={styles.title} data-testid="dialog-title">
            {t(title)}
          </mui.Typography>
        )}
        <mui.Typography variant="h5" className={styles.message} data-testid="dialog-message">
          {message}
        </mui.Typography>
      </div>
    </div>
  );

  const buttons = () => (
    <>
      <Button onClick={onConfirm} variant="contained" color="primary" disabled={disabled}>
        {t(confirmButtonLabel)}
      </Button>
      {cancelButtonLabel && <Button onClick={onCancel}>{t(cancelButtonLabel)}</Button>}
    </>
  );

  return <BaseDialog isVisible={isVisible} onClose={onCancel} Title={TitleSection} Actions={buttons} />;
};

export default ConfirmDialog;
